import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../../store/store";
import { STATUS } from "../../../store/slices/OrganizationSlice";
import {
  DeactivateResult,
  OrganizationV3,
  resetDeactivationState,
} from "../../../store/slices/OrganizationsV3Slice";
import {
  deactivateOrganizationAsync,
  fetchDeactivationPreviewAsync,
} from "../../../store/thunks/OrganizationsV3Thunk";
import { Dialog, DialogActions, DialogBody, DialogDescription, DialogTitle } from "../../Catalyst/dialog";
import { Checkbox, CheckboxField, CheckboxGroup } from "../../Catalyst/checkbox";
import { Description, Label } from "../../Catalyst/fieldset";
import { Button } from "../../Catalyst/button";
import { useToast } from "../../../contexts/ToastContext";

const ButtonSpinner = () => (
  <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
    <path
      className="opacity-75"
      fill="currentColor"
      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
    />
  </svg>
);

interface DeactivateOrgDialogProps {
  organization: OrganizationV3 | null;
  open: boolean;
  onClose: () => void;
  // Called after a successful deactivation so the parent refetches the list and stats.
  onDeactivated: () => void;
}

const DeactivateOrgDialog = ({
  organization,
  open,
  onClose,
  onDeactivated,
}: DeactivateOrgDialogProps) => {
  const dispatch = useDispatch<AppDispatch>();
  const { showSuccessToast } = useToast();
  const { deactivationPreview, previewStatus, previewError, deactivateStatus, deactivateError } =
    useSelector((state: RootState) => state.OrganizationsV3Store);

  const [cascadeSubOrgs, setCascadeSubOrgs] = useState(true);
  const [cascadeUsers, setCascadeUsers] = useState(true);

  const isDeactivating = deactivateStatus === STATUS.LOADING;
  const isPreviewLoading = previewStatus === STATUS.LOADING || previewStatus === STATUS.IDLE;
  // Only trust preview counts that belong to the org this dialog is showing.
  const preview =
    deactivationPreview?.organizationId === organization?.id ? deactivationPreview : null;

  // Load fresh cascade counts and reset the checkboxes each time the dialog opens.
  useEffect(() => {
    if (open && organization) {
      dispatch(resetDeactivationState());
      setCascadeSubOrgs(true);
      setCascadeUsers(true);
      dispatch(fetchDeactivationPreviewAsync(organization.id));
    }
  }, [open, organization, dispatch]);

  const handleConfirm = async () => {
    if (!organization || isDeactivating) return;
    const result = await dispatch(
      deactivateOrganizationAsync({
        orgId: organization.id,
        body: { cascadeSubOrgs, cascadeUsers },
      })
    );
    if (!deactivateOrganizationAsync.fulfilled.match(result)) {
      // deactivateError is rendered inline; keep the dialog open for a retry.
      return;
    }
    const outcome = result.payload as DeactivateResult;
    // An already-deactivated org is a no-op; the API treats it as success and so do we.
    showSuccessToast(
      "Organization deactivated",
      outcome.alreadyDeactivated
        ? `${organization.name} was already deactivated.`
        : `${organization.name} was deactivated (${outcome.deactivatedOrganizations} organizations, ${outcome.deactivatedUsers} users).`
    );
    onClose();
    onDeactivated();
  };

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>Deactivate {organization?.name}?</DialogTitle>
      <DialogDescription>
        This will immediately deactivate the organization and it cannot be undone from this app.
        Large organizations may take a moment to process.
      </DialogDescription>
      <DialogBody>
        {isPreviewLoading ? (
          <div className="flex items-center gap-3 py-2 text-sm text-textColor">
            <ButtonSpinner />
            Loading affected counts...
          </div>
        ) : (
          <>
            {previewError && (
              <p className="mb-4 text-sm text-amber-600">
                Could not load the affected sub-organization and user counts.
              </p>
            )}
            <CheckboxGroup>
              <CheckboxField>
                <Checkbox
                  checked={cascadeSubOrgs}
                  onChange={setCascadeSubOrgs}
                  disabled={isDeactivating}
                />
                <Label>
                  Deactivate all sub-organizations
                  {preview ? ` (${preview.subOrgCount} sub-orgs)` : ""}
                </Label>
              </CheckboxField>
              <CheckboxField>
                <Checkbox
                  checked={cascadeUsers}
                  onChange={setCascadeUsers}
                  disabled={isDeactivating}
                />
                <Label>
                  Deactivate all users
                  {preview ? ` (${preview.userCount} users)` : ""}
                </Label>
                <Description>
                  Includes every invited and signed-up user in the affected organizations.
                </Description>
              </CheckboxField>
            </CheckboxGroup>
          </>
        )}
        {deactivateError && (
          <p className="mt-4 text-sm font-medium text-red-500">{deactivateError}</p>
        )}
      </DialogBody>
      <DialogActions>
        <Button plain onClick={onClose} disabled={isDeactivating}>
          Cancel
        </Button>
        <Button color="red" onClick={handleConfirm} disabled={isDeactivating}>
          {isDeactivating && <ButtonSpinner />}
          {isDeactivating ? "Deactivating..." : "Deactivate"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DeactivateOrgDialog;
